// 수비 화면의 구독 — **한 벌을 세 화면이 나눠 쓴다**
//
// 적 rally 등록 폼(작전 구성 defense 탭 좌열), 착탄순 시퀀스(작전 구성 defense 탭 우열),
// roster 아코디언(defense 열람)이 같은 값을 본다. 각자 읽으면 한 화면이 새 줄을 알고 다른
// 화면이 모르는 순간이 생기고, 그 어긋남은 「등록이 안 됐다」로 보인다 — rally 편집기가
// 좌열과 우열을 같은 상태 위에 묶은 것과 같은 이유다.
//
// ── 소켓은 빌려 쓴다 ────────────────────────────────────────────────────────
//
// `subscribeServerEvents` 가 토큰당 소켓 하나를 여러 구독자에게 나눠 준다. 화면마다 자기
// 소켓을 여는 것이 자연스러운 욕구인데, 그렇게 하면 서버의 연결 표가 세션 토큰당 소켓
// 하나만 들기 때문에 **둘이 서로를 쫓아내며 2초마다 깜빡인다** — 2026-08-13 에 작전 구성
// 화면에서 실제로 났고, 화면에는 「가끔 반영이 2초 늦는다」로만 보였다.
//
// ── 명부를 왜 다시 읽는가 ───────────────────────────────────────────────────
//
// **적 rally 의 착탄은 명부의 적 marching 에 기댄다.** 그래서 명부가 바뀌면 적 rally 표가
// 한 줄도 안 바뀌었어도 화면의 숫자는 달라져야 한다. 서버가 그 순간 `defense.updated` 를
// 내보내고(수비 라우트가 명부 변경을 구독한다), 이 훅은 그 방송을 받으면 **명부도 함께
// 다시 읽는다.** 계약에 `rosters.updated` 가 선언돼 있지만 서버 어디에도 발신부가 없어서,
// 명부의 지금 값을 얻는 길이 이 되읽기 하나다. 왕복이 하나 늘지만 CT 가 명부를 고치는
// 순간에만 일어난다.
//
// ── 재접속에서 통째로 다시 읽는다 ───────────────────────────────────────────
//
// 끊겨 있던 동안의 등록·재앵커·삭제를 놓치면 화면은 **낡은 목록을 지금 값이라 믿고** 조용히
// 앉아 있는다. 그 침묵이 이 제품에서 가장 나쁜 실패다. 작전 구성 화면과 열람 화면이 같은
// 구멍을 각각 한 번씩 메웠고, 여기서는 처음부터 막고 짓는다.

import { useCallback, useEffect, useRef, useState } from 'react'

import type { EnemyRally, RoomPublic, Roster } from '../../contract/schema.ts'
// 계약의 `EnemyPhase` 는 TypeBox **값**이라 타입 자리에 쓸 수 없다. 단계의 타입은 코어가
// 갖고 있고 서버 층도 같은 자리를 읽는다 — 두 곳이 다른 이름을 쓰면 같은 값이 두 타입이 된다.
import type { EnemyPhase } from '../../core/timing/defense.ts'
import { call, routes, subscribeServerEvents } from '../access/client.ts'
import { accept, issue, newReadOrder } from '../access/read-order.ts'

export interface EnemyRallyDraft {
  enemyRosterId: string
  phase: EnemyPhase
  remainingMs: number
}

export interface DefenseStore {
  /**
   * 첫 응답이 왔는가. **껍데기와 값을 가르는 자리다** — 같은 표식을 두 갈래에 달면 그
   * 표식은 「화면이 있다」만 말하고 「값이 있다」는 말하지 않는다. 브라우저 검사가
   * 2026-08-14 에 정확히 그 함정으로 회차마다 다른 시나리오를 죽였다.
   */
  loaded: boolean
  enemyRallies: readonly EnemyRally[]
  rosters: readonly Roster[]
  room: RoomPublic | null
  connected: boolean
  sessionGone: boolean
  addEnemyRally: (draft: EnemyRallyDraft) => Promise<string | null>
  reanchor: (
    enemyRallyId: string,
    draft: Omit<EnemyRallyDraft, 'enemyRosterId'>,
  ) => Promise<string | null>
  removeEnemyRally: (enemyRallyId: string) => Promise<string | null>
}

export function useDefense(token: string | null): DefenseStore {
  const [enemyRallies, setEnemyRallies] = useState<readonly EnemyRally[]>([])
  const [rosters, setRosters] = useState<readonly Roster[]>([])
  const [room, setRoom] = useState<RoomPublic | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [connected, setConnected] = useState(false)
  const [sessionGone, setSessionGone] = useState(false)

  // 콜백이 매 렌더마다 새로 만들어져 구독이 끊겼다 붙는 것을 막는다. 재구독마다 소켓이
  // 갈리면 그 순간의 방송을 놓치고, 놓친 화면에는 그럴듯한 옛 숫자가 남는다.
  const tokenRef = useRef(token)
  tokenRef.current = token

  // **늦게 온 옛 명부 응답이 새 값을 덮지 않게 한다** (2026-09-28). 이 화면도 방송마다 명부를
  // 다시 읽는다. 규칙과 까닭은 `src/web/access/read-order.ts` 가 진다.
  const rosterOrder = useRef(newReadOrder())

  const readRosters = useCallback(async (): Promise<void> => {
    const current = tokenRef.current
    if (current === null) return
    const ticket = issue(rosterOrder.current)
    const result = await call<{ rosters: Roster[] }>(routes.rosters, { token: current })
    if (result.ok && accept(rosterOrder.current, ticket)) setRosters(result.data.rosters)
  }, [])

  const readAll = useCallback(async (): Promise<void> => {
    const current = tokenRef.current
    if (current === null) return
    const ticket = issue(rosterOrder.current)
    const [rallyResult, rosterResult, roomResult] = await Promise.all([
      call<{ enemyRallies: EnemyRally[] }>(routes.enemyRallies, { token: current }),
      call<{ rosters: Roster[] }>(routes.rosters, { token: current }),
      call<RoomPublic>(routes.roomPublic, { token: current }),
    ])
    if (rallyResult.ok) setEnemyRallies(rallyResult.data.enemyRallies)
    if (rosterResult.ok && accept(rosterOrder.current, ticket)) setRosters(rosterResult.data.rosters)
    if (roomResult.ok) setRoom(roomResult.data)
    setLoaded(true)
  }, [])

  useEffect(() => {
    if (token === null) return
    void readAll()

    return subscribeServerEvents(token, {
      onEvent: (message) => {
        if (message.kind === 'defense.updated' && message['enemyRallies'] !== undefined) {
          setEnemyRallies(message['enemyRallies'] as EnemyRally[])
          // **방송 하나에 되읽기 하나가 붙는다.** 이 방송은 적 rally 가 바뀌었을 때도 오고
          // **명부가 바뀌었을 때도** 오는데, 뒤의 경우 목록의 값은 그대로이고 달라진 것은
          // 명부다. 안 읽으면 착탄 미상이 영영 안 풀리고 화면은 정상으로 보인다.
          void readRosters()
          return
        }
        // 개명은 명부 방송이 아니라 접속 명단 방송으로 온다(이름 수정 라우트가 명단을 다시
        // 센다). 이름은 얼어붙는 값이 아니라 화면이 늘 지금 이름을 말해야 한다.
        if (message.kind === 'presence.updated') {
          void readRosters()
          return
        }
        if (message.kind === 'room.state' && message['room'] !== undefined) {
          setRoom(message['room'] as RoomPublic)
          return
        }
        // 유휴 초기화가 지나갔다. 무엇이 지워졌는지 묻지 말고 통째로 다시 읽는다 — 지워진
        // 목록을 화면이 그대로 들고 있으면 없는 적의 카운트다운이 흐른다.
        if (message.kind === 'room.reset') void readAll()
      },
      onStatus: (status) => {
        setConnected(status.connected)
        setSessionGone(status.sessionGone)
        if (status.connected) void readAll()
      },
    })
  }, [token, readAll, readRosters])

  /**
   * 쓰는 문 셋이 같은 모양이다 — **응답이 목록 한 벌이고 그것을 그대로 받는다.**
   *
   * 조작마다 다른 병합 코드를 두면 그 코드들이 서로 다르게 낡는다. 실패 문구는 서버의
   * 것을 그대로 쓴다: 화면이 자기 말로 다시 적으면 막은 이유와 화면이 말하는 이유가 갈린다.
   */
  const write = useCallback(
    async (route: { method: string; path: string }, body: unknown): Promise<string | null> => {
      const current = tokenRef.current
      if (current === null) return '입장하지 않은 화면입니다'
      const result = await call<{ enemyRallies: EnemyRally[] }>(route, { body, token: current })
      if (!result.ok) return result.message
      setEnemyRallies(result.data.enemyRallies)
      return null
    },
    [],
  )

  const addEnemyRally = useCallback(
    async (draft: EnemyRallyDraft): Promise<string | null> => write(routes.enemyRallyAdd, draft),
    [write],
  )

  const reanchor = useCallback(
    async (
      enemyRallyId: string,
      draft: Omit<EnemyRallyDraft, 'enemyRosterId'>,
    ): Promise<string | null> => write(routes.enemyRallyReanchor, { enemyRallyId, ...draft }),
    [write],
  )

  const removeEnemyRally = useCallback(
    async (enemyRallyId: string): Promise<string | null> =>
      write(routes.enemyRallyRemove, { enemyRallyId }),
    [write],
  )

  return {
    loaded,
    enemyRallies,
    rosters,
    room,
    connected,
    sessionGone,
    addEnemyRally,
    reanchor,
    removeEnemyRally,
  }
}
